import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { formatAmount } from '../../lib/money';
import {
  Badge,
  Banner,
  Button,
  Card,
  DataTable,
  EmptyState,
  KpiTile,
  PageContainer,
  PageHeader,
  Select,
  Spinner,
  CategoryBarChart,
  DonutChart,
  TrendAreaChart,
} from '../../ui/components';
import { Icon } from '../../ui/icons';
import { useProperty } from '../../app/PropertyContext';
import { useT } from '../../i18n';
import { poStatusMeta } from '../purchase-orders/purchaseOrdersApi';
import { reportsApi, RANGE_PRESETS, resolveRange } from './reportsApi';
import './reports.css';

const fmtMoney = (currency, amount) => `${currency} ${formatAmount(amount)}`;
const fmtCompact = (n) =>
  Math.abs(Number(n || 0)) >= 1000 ? `${(Number(n) / 1000).toFixed(1)}k` : `${Number(n || 0).toFixed(0)}`;
const fmtDays = (d) => (d == null ? '—' : `${Number(d).toFixed(1)}d`);
const monthLabel = (ym) => {
  const [y, m] = (ym || '').split('-').map(Number);
  if (!y || !m) return ym;
  return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: 'short', year: '2-digit' });
};

export default function ReportsPage() {
  const t = useT();
  const { activePropertyId, activeProperty, properties } = useProperty();
  const [range, setRange] = useState('12m');
  const [spend, setSpend] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [savings, setSavings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const params = resolveRange(range);
    try {
      const [s, k, sv] = await Promise.all([
        reportsApi.spend(params),
        reportsApi.kpis(params),
        reportsApi.savings(params),
      ]);
      setSpend(s);
      setKpis(k);
      setSavings(sv);
    } catch (err) {
      setError(err?.message || t('Failed to load reports.'));
    } finally {
      setLoading(false);
    }
    // activePropertyId drives a refetch: the API scopes by the active property header.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, activePropertyId, t]);

  useEffect(() => { load(); }, [load]);

  const currency =
    spend?.currency ||
    savings?.currency ||
    activeProperty?.baseCurrency ||
    (properties?.length === 1 ? properties[0]?.baseCurrency : null) ||
    'USD';

  const trendData = useMemo(
    () => (spend?.monthlyTrend || []).map((b) => ({ label: monthLabel(b.month), total: b.total, orders: b.orderCount })),
    [spend],
  );
  const supplierData = useMemo(
    () => (spend?.bySupplier || []).map((s) => ({ label: s.supplierName, total: s.total })),
    [spend],
  );
  const categoryData = useMemo(
    () => (spend?.byCategory || []).map((c) => ({ name: c.categoryName, value: c.total })),
    [spend],
  );

  const headerActions = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', minWidth: 200 }}>
      <Select
        value={range}
        onChange={setRange}
        ariaLabel={t('Reporting period')}
        options={RANGE_PRESETS.map((p) => ({ value: p.value, label: t(p.label) }))}
      />
      <Button variant="ghost" size="sm" leadingIcon={<Icon.Refresh size={15} />} onClick={load} aria-label={t('Refresh')} />
    </div>
  );

  return (
    <PageContainer size="full">
      <PageHeader
        title={t('Reports')}
        description={`${t('Spend analytics, procurement KPIs and savings')}${activeProperty ? ` · ${activeProperty.name}` : ` · ${t('all properties')}`}`}
        icon={<Icon.BarChart size={22} />}
        actions={headerActions}
      />

      {error ? (
        <EmptyState
          icon={<Icon.CircleAlert size={28} />}
          title={t("Couldn't load reports")}
          description={error}
          action={<Button variant="secondary" onClick={load}>{t('Retry')}</Button>}
        />
      ) : loading ? (
        <div style={{ display: 'grid', placeItems: 'center', padding: 'var(--space-16)' }}><Spinner size={26} /></div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          {spend?.hasMixedCurrencies && (
            <Banner tone="info">
              {t('{count} order(s) in other currencies are excluded — figures are shown in {currency}.', { count: spend.excludedCurrencyOrderCount, currency })}
            </Banner>
          )}

          <div className="rpt-kpis">
            <KpiTile label={t('Committed spend')} value={fmtMoney(currency, spend?.totalSpend)} meta={t('{count} orders', { count: spend?.orderCount || 0 })} />
            <KpiTile label={t('Avg order value')} value={fmtMoney(currency, spend?.averageOrderValue)} meta={t('Largest {amount}', { amount: fmtMoney(currency, spend?.largestOrderValue) })} />
            <KpiTile label={t('Open commitments')} value={fmtMoney(currency, spend?.openCommitmentsValue)} meta={t('{count} open orders', { count: spend?.openCommitmentsCount || 0 })} />
            <KpiTile label={t('Est. price savings')} value={fmtMoney(currency, savings?.estimatedPriceSavings)} meta={t('vs historical peak')} />
          </div>

          <Card>
            <Card.Header>
              <Card.Title>{t('Spend trend')}</Card.Title>
              <div className="pp-cell-muted" style={{ fontSize: 'var(--text-sm)', marginTop: 2 }}>
                {t('Committed purchase-order value by month.')}
              </div>
            </Card.Header>
            <Card.Body>
              {trendData.some((d) => d.total > 0) ? (
                <TrendAreaChart
                  data={trendData}
                  xKey="label"
                  dataKey="total"
                  name={t('Spend')}
                  height={300}
                  formatValue={(v) => fmtMoney(currency, v)}
                  formatYAxis={fmtCompact}
                />
              ) : (
                <EmptyState icon={<Icon.BarChart size={26} />} title={t('No spend in this period')} description={t('Try a wider date range.')} />
              )}
            </Card.Body>
          </Card>

          <div className="rpt-2col">
            <Card>
              <Card.Header><Card.Title>{t('Top suppliers')}</Card.Title></Card.Header>
              <Card.Body>
                {supplierData.length > 0 ? (
                  <CategoryBarChart
                    data={supplierData}
                    xKey="label"
                    dataKey="total"
                    name={t('Spend')}
                    height={300}
                    formatValue={(v) => fmtMoney(currency, v)}
                    formatYAxis={fmtCompact}
                  />
                ) : (
                  <EmptyState icon={<Icon.Building size={26} />} title={t('No supplier spend')} />
                )}
              </Card.Body>
            </Card>

            <Card>
              <Card.Header><Card.Title>{t('Spend by category')}</Card.Title></Card.Header>
              <Card.Body>
                {categoryData.length > 0 ? (
                  <DonutChart
                    data={categoryData}
                    height={260}
                    formatValue={(v) => fmtMoney(currency, v)}
                    centerLabel={t('Total')}
                    centerValue={fmtCompact(spend?.totalSpend)}
                  />
                ) : (
                  <EmptyState icon={<Icon.Package size={26} />} title={t('No category data')} />
                )}
              </Card.Body>
            </Card>
          </div>

          <Card>
            <Card.Header>
              <Card.Title>{t('Procurement KPIs')}</Card.Title>
              <div className="pp-cell-muted" style={{ fontSize: 'var(--text-sm)', marginTop: 2 }}>
                {t('Average cycle times across the request-to-order flow, and current workload.')}
              </div>
            </Card.Header>
            <Card.Body>
              <div className="rpt-kpis">
                <KpiTile label={t('Requisition approval')} value={fmtDays(kpis?.reqSubmitToDecisionDays)} meta={t('submit → decision · n={n}', { n: kpis?.reqDecisionSample || 0 })} />
                <KpiTile label={t('PO approval')} value={fmtDays(kpis?.poSubmitToDecisionDays)} meta={t('submit → decision · n={n}', { n: kpis?.poDecisionSample || 0 })} />
                <KpiTile label={t('PO issue')} value={fmtDays(kpis?.poDecisionToSentDays)} meta={t('decision → sent · n={n}', { n: kpis?.poSentSample || 0 })} />
                <KpiTile label={t('Requisition → PO')} value={fmtDays(kpis?.reqToPoDays)} meta={t('created → PO · n={n}', { n: kpis?.reqToPoSample || 0 })} />
              </div>
              <div className="rpt-workload">
                <span><strong>{kpis?.openPurchaseOrders || 0}</strong> {t('open POs')}</span>
                <span><strong>{kpis?.pendingPoApprovals || 0}</strong> {t('POs awaiting approval')}</span>
                <span><strong>{kpis?.pendingRequisitionApprovals || 0}</strong> {t('requisitions awaiting approval')}</span>
                <span><strong>{kpis?.pendingReceipts || 0}</strong> {t('awaiting delivery')}</span>
              </div>
            </Card.Body>
          </Card>

          <Card>
            <Card.Header>
              <Card.Title>{t('Order status funnel')}</Card.Title>
            </Card.Header>
            <Card.Body>
              {(spend?.statusFunnel || []).length === 0 ? (
                <EmptyState icon={<Icon.ListChecks size={26} />} title={t('No orders in this period')} />
              ) : (
                <DataTable
                  columns={[
                    { key: 'status', header: t('Status'), render: (r) => { const m = poStatusMeta(r.status); return <Badge tone={m.tone} dot>{t(m.label)}</Badge>; } },
                    { key: 'count', header: t('Orders'), align: 'right', render: (r) => r.count },
                    { key: 'total', header: t('Value'), align: 'right', render: (r) => fmtMoney(currency, r.total) },
                  ]}
                  data={spend.statusFunnel}
                  rowKey={(r) => r.status}
                  density="compact"
                />
              )}
            </Card.Body>
          </Card>

          <Card>
            <Card.Header>
              <Card.Title>{t('Budget vs actual')}</Card.Title>
              <div className="pp-cell-muted" style={{ fontSize: 'var(--text-sm)', marginTop: 2 }}>
                {t('Allocated vs consumed for budgets active in this period. Positive variance = under budget.')}
              </div>
            </Card.Header>
            <Card.Body>
              {(savings?.budgets || []).length === 0 ? (
                <EmptyState icon={<Icon.Wallet size={26} />} title={t('No budgets in this period')} description={t('Set up budgets to track variance and savings.')} />
              ) : (
                <>
                  <div className="rpt-kpis" style={{ marginBottom: 'var(--space-3)' }}>
                    <KpiTile label={t('Allocated')} value={fmtMoney(currency, savings.totalAllocated)} />
                    <KpiTile label={t('Consumed')} value={fmtMoney(currency, savings.totalConsumed)} />
                    <KpiTile
                      label={t('Variance')}
                      value={fmtMoney(currency, savings.totalVariance)}
                      meta={savings.totalVariance >= 0 ? t('under budget') : t('over budget')}
                    />
                  </div>
                  <DataTable
                    columns={[
                      { key: 'cc', header: t('Cost centre'), render: (r) => <span>{r.costCenter}{r.glAccountCode ? ` / ${r.glAccountCode}` : ''}</span> },
                      { key: 'period', header: t('Period'), render: (r) => r.periodLabel },
                      { key: 'allocated', header: t('Allocated'), align: 'right', render: (r) => fmtMoney(r.currency, r.allocated) },
                      { key: 'consumed', header: t('Consumed'), align: 'right', render: (r) => fmtMoney(r.currency, r.consumed) },
                      {
                        key: 'variance', header: t('Variance'), align: 'right',
                        render: (r) => <span className={r.variance >= 0 ? 'rpt-pos' : 'rpt-neg'}>{fmtMoney(r.currency, r.variance)}</span>,
                      },
                    ]}
                    data={savings.budgets}
                    rowKey={(r) => r.budgetId}
                    density="compact"
                  />
                </>
              )}
            </Card.Body>
          </Card>

          <Card>
            <Card.Header>
              <Card.Title>{t('Price savings (indicative)')}</Card.Title>
              <div className="pp-cell-muted" style={{ fontSize: 'var(--text-sm)', marginTop: 2 }}>
                {t('Items whose latest unit price beat their historical peak, valued on volume bought this period. Indicative only — ignores spec/pack changes.')}
              </div>
            </Card.Header>
            <Card.Body>
              {(savings?.priceSavings || []).length === 0 ? (
                <EmptyState icon={<Icon.TrendingDown size={26} />} title={t('No price improvements detected')} description={t('Needs repeat purchases at a lower price than the historical high.')} />
              ) : (
                <DataTable
                  columns={[
                    { key: 'item', header: t('Item'), render: (r) => r.itemName },
                    { key: 'peak', header: t('Peak price'), align: 'right', render: (r) => fmtMoney(r.currency, r.maxUnitPrice) },
                    { key: 'latest', header: t('Latest price'), align: 'right', render: (r) => fmtMoney(r.currency, r.latestUnitPrice) },
                    { key: 'qty', header: t('Qty (period)'), align: 'right', render: (r) => r.recentQuantity },
                    { key: 'saving', header: t('Est. saving'), align: 'right', render: (r) => <span className="rpt-pos">{fmtMoney(r.currency, r.estimatedSaving)}</span> },
                  ]}
                  data={savings.priceSavings}
                  rowKey={(r) => r.itemId}
                  density="compact"
                />
              )}
            </Card.Body>
          </Card>
        </div>
      )}
    </PageContainer>
  );
}
